import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, ChevronUp, ChevronDown, Eye, EyeOff, Upload, Save, X, Pencil } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const PRESET_COLORS = ["#2eb6a8", "#e8478e", "#9c6dde", "#b9985f", "#c93875", "#1f8d83", "#1c1815"];

export default function CategoriesManager({ onChange }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", image_url: "", accent_color: PRESET_COLORS[0] });
  const fileRef = useRef(null);

  const fetchAll = async () => {
    const { data } = await api.get("/admin/categories");
    setItems(data);
  };

  useEffect(() => { fetchAll(); }, []);

  const startNew = () => {
    setAdding(true);
    setEditing(null);
    setForm({ name: "", image_url: "", accent_color: PRESET_COLORS[0] });
  };
  const startEdit = (c) => {
    setAdding(false);
    setEditing(c.id);
    setForm({ name: c.name, image_url: c.image_url || "", accent_color: c.accent_color || PRESET_COLORS[0] });
  };
  const cancel = () => { setAdding(false); setEditing(null); setForm({ name: "", image_url: "", accent_color: PRESET_COLORS[0] }); };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error("Image too large"); e.target.value = ""; return; }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    }
    e.target.value = "";
  };

  const save = async () => {
    const name = (form.name || "").trim();
    if (!name) { toast.error("Name required"); return; }
    try {
      if (editing) {
        await api.patch(`/admin/categories/${editing}`, { name, image_url: form.image_url, accent_color: form.accent_color });
        toast.success("Category updated");
      } else {
        await api.post("/admin/categories", { name, image_url: form.image_url, accent_color: form.accent_color });
        toast.success("Category added");
      }
      await fetchAll();
      if (onChange) onChange();
      cancel();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    }
  };

  const toggleEnabled = async (c) => {
    try {
      await api.patch(`/admin/categories/${c.id}`, { enabled: !c.enabled });
      await fetchAll();
      if (onChange) onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[idx], next[target]] = [next[target], next[idx]];
    setItems(next.map((c, i) => ({ ...c, position: i })));
    try {
      await api.post("/admin/categories/reorder", { items: next.map((c, i) => ({ id: c.id, position: i })) });
      if (onChange) onChange();
    } catch (err) {
      toast.error("Reorder failed");
      fetchAll();
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? Products in this category will be marked as Uncategorized.`)) return;
    try {
      const { data } = await api.delete(`/admin/categories/${c.id}`);
      toast.success(data.products_reassigned ? `Deleted. ${data.products_reassigned} product(s) marked Uncategorized.` : "Deleted");
      await fetchAll();
      if (onChange) onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div data-testid="categories-manager" className="mt-8">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-display text-3xl font-extrabold text-[#1c1815]">Categories</h2>
          <p className="text-sm text-[#4a4239] mt-1">Add, rename, recolor, reorder, or hide categories. Customers see these on the homepage and shop filters.</p>
        </div>
        {!adding && !editing && (
          <button type="button" onClick={startNew} data-testid="category-add"
            className="inline-flex items-center gap-1.5 bg-[#e8478e] hover:bg-[#c93875] text-white font-bold uppercase tracking-[0.2em] text-xs px-4 py-2.5 rounded-full">
            <Plus size={14}/> Add Category
          </button>
        )}
      </div>

      {(adding || editing) && (
        <div className="mb-5 p-5 rounded-2xl border-2 border-[#e8478e] bg-white shadow-sm" data-testid="category-form">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display text-xl font-extrabold text-[#1c1815]">{editing ? "Edit Category" : "New Category"}</div>
            <button type="button" onClick={cancel} className="text-[#8a8076] hover:text-[#c93875]"><X size={18}/></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Stickers"
                data-testid="category-name"
                className="mt-1.5 w-full bg-[#faf4e9] border border-[#e5dccd] focus:border-[#2eb6a8] rounded-xl px-4 py-3 outline-none text-[#1c1815]"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Accent Color</label>
              <div className="mt-1.5 flex flex-wrap gap-2 items-center">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, accent_color: c })}
                    data-testid={`color-preset-${c}`}
                    title={c}
                    className={`w-9 h-9 rounded-full border-2 transition-all ${form.accent_color === c ? "border-[#1c1815] scale-110 shadow-md" : "border-white"}`}
                    style={{ backgroundColor: c }}
                  />
                ))}
                <input
                  type="color"
                  value={form.accent_color}
                  onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                  data-testid="color-custom"
                  className="w-9 h-9 rounded-full cursor-pointer border-0 bg-transparent"
                  title="Pick custom color"
                />
              </div>
            </div>
          </div>
          <div className="mt-4">
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Image</label>
            <div className="mt-1.5 flex items-center gap-3">
              <div
                className="w-20 h-20 rounded-full overflow-hidden border-2 bg-[#faf4e9] flex items-center justify-center"
                style={{ borderColor: form.accent_color }}
              >
                {form.image_url ? (
                  <img src={form.image_url} alt="" className="w-full h-full object-cover"/>
                ) : (
                  <span className="text-3xl font-display font-black" style={{ color: form.accent_color }}>{(form.name || "?").slice(0, 1).toUpperCase()}</span>
                )}
              </div>
              <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs font-bold uppercase tracking-[0.18em] bg-[#faf4e9] hover:bg-[#efe5d4] border border-[#e5dccd] text-[#1c1815] px-4 py-2.5 rounded-xl">
                <Upload size={14}/> {form.image_url ? "Change Image" : "Upload Image"}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="category-upload"/>
              </label>
              {form.image_url && (
                <button type="button" onClick={() => setForm({ ...form, image_url: "" })}
                  className="text-xs text-[#c93875] hover:text-[#e8478e] font-bold uppercase tracking-[0.18em]">Remove</button>
              )}
            </div>
            <p className="text-[11px] text-[#8a8076] mt-2">Optional. If no image, we'll show the first letter of the name on the accent color.</p>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" onClick={cancel}
              className="text-xs font-bold uppercase tracking-[0.18em] text-[#8a8076] hover:text-[#1c1815] px-4 py-2.5">Cancel</button>
            <button type="button" onClick={save} data-testid="category-save"
              className="inline-flex items-center gap-1.5 bg-[#2eb6a8] hover:bg-[#1f8d83] text-white font-bold uppercase tracking-[0.2em] text-xs px-5 py-2.5 rounded-full">
              <Save size={14}/> {editing ? "Save Changes" : "Create"}
            </button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-center text-sm text-[#8a8076] py-10 rounded-2xl border border-dashed border-[#e5dccd]">
          No categories yet. Click "Add Category" to start.
        </div>
      ) : (
        <div className="space-y-2" data-testid="categories-list">
          {items.map((c, idx) => (
            <div
              key={c.id}
              className={`flex items-center gap-3 p-3 rounded-2xl border bg-white ${c.enabled ? "border-[#e5dccd]" : "border-[#e5dccd] opacity-60"}`}
              data-testid={`category-row-${c.slug}`}
            >
              <div className="flex flex-col">
                <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0}
                  data-testid={`category-up-${c.slug}`}
                  className="text-[#8a8076] hover:text-[#1c1815] disabled:opacity-30 p-0.5"><ChevronUp size={16}/></button>
                <button type="button" onClick={() => move(idx, 1)} disabled={idx === items.length - 1}
                  data-testid={`category-down-${c.slug}`}
                  className="text-[#8a8076] hover:text-[#1c1815] disabled:opacity-30 p-0.5"><ChevronDown size={16}/></button>
              </div>
              <div className="w-12 h-12 rounded-full overflow-hidden border-2 bg-[#faf4e9] flex items-center justify-center flex-shrink-0"
                style={{ borderColor: c.accent_color }}>
                {c.image_url ? (
                  <img src={c.image_url} alt={c.name} className="w-full h-full object-cover"/>
                ) : (
                  <span className="text-lg font-display font-black" style={{ color: c.accent_color }}>{c.name.slice(0, 1).toUpperCase()}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[#1c1815] truncate">{c.name}</div>
                <div className="text-xs text-[#8a8076]">/{c.slug}</div>
              </div>
              <button type="button" onClick={() => toggleEnabled(c)} title={c.enabled ? "Hide from store" : "Show on store"}
                data-testid={`category-toggle-${c.slug}`}
                className="text-[#8a8076] hover:text-[#1f8d83] p-2 rounded-lg hover:bg-[#2eb6a8]/10">
                {c.enabled ? <Eye size={16}/> : <EyeOff size={16}/>}
              </button>
              <button type="button" onClick={() => startEdit(c)} title="Edit"
                data-testid={`category-edit-${c.slug}`}
                className="text-[#8a8076] hover:text-[#2eb6a8] p-2 rounded-lg hover:bg-[#2eb6a8]/10">
                <Pencil size={15}/>
              </button>
              <button type="button" onClick={() => remove(c)} title="Delete"
                data-testid={`category-delete-${c.slug}`}
                className="text-[#8a8076] hover:text-[#c93875] p-2 rounded-lg hover:bg-[#c93875]/10">
                <Trash2 size={15}/>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
